import { useState, useEffect, useCallback } from 'react';
import {
  listJobDocuments, createProjectDocument, updateProjectDocument,
  updateDocumentStatus, deleteProjectDocument, getDocumentRenderUrl,
  type ProjectDocument,
} from '@/lib/api/projectDocuments';

interface ContractBuilderTabProps {
  jobId: string | number;
  jobTitle?: string;
  jobNumber?: string;
  clientName?: string;
  clientAddress?: string;
  siteAddress?: string;
  startDate?: string;
  endDate?: string;
  budget?: number;
}

interface ContractForm {
  title: string;
  effective_date: string;
  scope_summary: string;
  contract_price: string;
  deposit_amount: string;
  deposit_due: string;
  progress_amount: string;
  progress_due: string;
  final_amount: string;
  final_due: string;
  start_date: string;
  end_date: string;
  warranty_period: string;
  additional_terms: string;
}

const fmt = (n: number) => `$${n.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d: string) => d ? new Date(d + 'T12:00:00').toLocaleDateString('en-CA', { month: 'long', day: 'numeric', year: 'numeric' }) : '—';

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-600',
  issued: 'bg-blue-100 text-blue-700',
  signed: 'bg-green-100 text-green-700',
  void: 'bg-red-100 text-red-600',
};

function generateContractHtml(form: ContractForm, props: ContractBuilderTabProps): string {
  const price = parseFloat(form.contract_price) || 0;
  const deposit = parseFloat(form.deposit_amount) || 0;
  const progress = parseFloat(form.progress_amount) || 0;
  const final = parseFloat(form.final_amount) || 0;
  const paymentRows = [
    deposit > 0 ? `<tr><td>Deposit</td><td>${fmt(deposit)}</td><td>${form.deposit_due || 'Upon signing'}</td></tr>` : '',
    progress > 0 ? `<tr><td>Progress payment</td><td>${fmt(progress)}</td><td>${form.progress_due || 'Per milestone'}</td></tr>` : '',
    final > 0 ? `<tr><td>Final payment</td><td>${fmt(final)}</td><td>${form.final_due || 'Upon substantial completion'}</td></tr>` : '',
  ].filter(Boolean).join('');

  return `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8">
<title>${form.title}</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=Manrope:wght@400;600;700;800&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
  body{font-family:'Manrope',Arial,sans-serif;font-size:10.5pt;color:#2C1810;background:#fff}
  @media print{body{font-size:10pt}.no-print{display:none}}
  .page{max-width:850px;margin:0 auto;padding:36px 48px}
  .header{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid #C77D3F;padding-bottom:18px;margin-bottom:24px}
  .brand-name{font-size:22pt;font-weight:800;color:#C77D3F;letter-spacing:-0.5px}
  .brand-sub{font-size:9.5pt;font-weight:700;color:#8B5A2B}
  .brand-tag{font-size:7.5pt;color:#8B5A2B;font-style:italic;margin-top:2px}
  .brand-contact{font-size:8.5pt;color:#5a4030;line-height:1.6;text-align:right}
  .doc-title{font-size:16pt;font-weight:800;color:#8B5A2B;margin-bottom:4px}
  .doc-meta{font-size:8.5pt;color:#888;margin-bottom:24px}
  .parties{display:grid;grid-template-columns:1fr 1fr;gap:20px;margin-bottom:24px}
  .party-box{border:1px solid #e0d0c0;border-radius:6px;padding:14px 16px;background:#FAF6F0}
  .party-label{font-size:7pt;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#C77D3F;margin-bottom:6px}
  .party-name{font-size:11pt;font-weight:700;color:#2C1810}
  .party-detail{font-size:8.5pt;color:#5a4030;line-height:1.6;margin-top:4px}
  h2{font-size:11pt;font-weight:800;color:#8B5A2B;margin:22px 0 8px;text-transform:uppercase;letter-spacing:0.5px;border-bottom:1px solid #e0d0c0;padding-bottom:4px}
  p{margin-bottom:8px;line-height:1.65}
  ul{margin:6px 0 10px 18px}
  li{margin-bottom:4px;line-height:1.6}
  table{width:100%;border-collapse:collapse;margin:10px 0 18px;font-size:9.5pt}
  th{background:#8B5A2B;color:#fff;font-weight:700;padding:7px 10px;text-align:left}
  td{padding:6px 10px;border-bottom:1px solid #e8ddd5;vertical-align:top}
  tr:nth-child(even) td{background:#FAF6F0}
  .total-row td{font-weight:700;border-top:2px solid #C77D3F;background:#fff4e8!important}
  .highlight-box{background:#fff4e8;border-left:4px solid #C77D3F;padding:12px 16px;margin:14px 0;border-radius:0 6px 6px 0}
  .notice-box{background:#fef9e7;border:1px solid #D69E2E;border-radius:6px;padding:12px 16px;margin:14px 0;font-size:9pt}
  .sig-block{display:grid;grid-template-columns:1fr 1fr;gap:40px;margin-top:48px}
  .sig-line{border-top:1px solid #2C1810;padding-top:6px;margin-top:36px;font-size:9pt;color:#5a4030}
  .sig-label{font-size:8pt;color:#888;margin-top:3px}
  .footer{margin-top:36px;border-top:1px solid #e0d0c0;padding-top:10px;font-size:7.5pt;color:#999;text-align:center}
</style>
</head><body><div class="page">

<div class="header">
  <div>
    <div class="brand-name">OchrePoint</div>
    <div class="brand-sub">Contracting LTD.</div>
    <div class="brand-tag">Built on Skill, Finished with Pride</div>
  </div>
  <div class="brand-contact">
    1888 Frances Street, Duncan, BC&nbsp;V9L 5A2<br>
    250 510 0677&nbsp;·&nbsp;emartin@ochrepoint.ca<br>
    ochrepoint.ca
  </div>
</div>

<div class="doc-title">Work Agreement</div>
<div class="doc-meta">Project: ${props.jobTitle || ''}${props.jobNumber ? '&nbsp;|&nbsp;Job No. ' + props.jobNumber : ''}&nbsp;|&nbsp;Effective: ${fmtDate(form.effective_date)}</div>

<div class="parties">
  <div class="party-box">
    <div class="party-label">Contractor</div>
    <div class="party-name">OchrePoint Contracting LTD.</div>
    <div class="party-detail">1888 Frances Street, Duncan, BC V9L 5A2<br>250 510 0677 · emartin@ochrepoint.ca</div>
  </div>
  <div class="party-box">
    <div class="party-label">Client</div>
    <div class="party-name">${props.clientName || ''}</div>
    <div class="party-detail">${props.clientAddress ? props.clientAddress + '<br>' : ''}${props.siteAddress ? 'Site: ' + props.siteAddress : ''}</div>
  </div>
</div>

<h2>1. Scope of Work</h2>
<p>${(form.scope_summary || '').replace(/\n/g, '</p><p>')}</p>

<h2>2. Contract Price &amp; Payment Schedule</h2>
<div class="highlight-box"><strong>Total Contract Price: ${fmt(price)}</strong><br>All amounts in Canadian dollars. Applicable taxes are not included unless stated above.</div>
${paymentRows ? `<table><thead><tr><th>Payment</th><th>Amount</th><th>Due When</th></tr></thead><tbody>${paymentRows}<tr class="total-row"><td>Total</td><td>${fmt(price)}</td><td></td></tr></tbody></table>` : ''}

<h2>3. Project Timeline</h2>
<p><strong>Estimated start date:</strong> ${fmtDate(form.start_date)}<br>
<strong>Estimated completion date:</strong> ${fmtDate(form.end_date)}</p>
<p>Timeline is subject to change due to weather, material lead times, client-requested changes, or other conditions beyond the Contractor's reasonable control. Any significant schedule change will be communicated in writing.</p>

<h2>4. Change Orders</h2>
<p>Any work outside the agreed scope of this contract requires a signed change order before commencement. Change orders will include a revised price and, where applicable, an adjusted timeline. No verbal authorizations will be honoured.</p>

<h2>5. Warranty</h2>
<p>OchrePoint Contracting LTD. warrants all workmanship against defects for <strong>${form.warranty_period || '1 year'}</strong> from the date of substantial completion. This warranty does not cover damage arising from misuse, neglect, acts of nature, or work performed by others. Material warranties are passed through from the manufacturer.</p>

${form.additional_terms ? `<h2>6. Additional Terms &amp; Conditions</h2><p>${form.additional_terms.replace(/\n/g, '</p><p>')}</p>` : ''}

<div class="notice-box">This agreement constitutes the entire understanding between the parties. Any modifications must be in writing and signed by both parties. By signing below, both parties agree to the terms set out herein.</div>

<div class="sig-block">
  <div>
    <div class="sig-line">________________________________</div>
    <div class="sig-label">Authorized Signature — OchrePoint Contracting LTD.</div>
    <div style="margin-top:14px;font-size:9pt;color:#5a4030">Name: ____________________________<br>Date: _____________________________</div>
  </div>
  <div>
    <div class="sig-line">________________________________</div>
    <div class="sig-label">Client Signature</div>
    <div style="margin-top:14px;font-size:9pt;color:#5a4030">Name: ____________________________<br>Date: _____________________________</div>
  </div>
</div>

<div class="footer">OchrePoint Contracting LTD. · 1888 Frances Street, Duncan, BC V9L 5A2 · 250 510 0677 · ochrepoint.ca<br>Work Agreement · ${props.jobNumber || ''} · ${props.jobTitle || ''}</div>
</div></body></html>`;
}

const EMPTY_FORM = (props: ContractBuilderTabProps): ContractForm => ({
  title: `Work Agreement — ${props.jobTitle || 'Project'}`,
  effective_date: new Date().toISOString().split('T')[0],
  scope_summary: '',
  contract_price: props.budget ? String(props.budget) : '',
  deposit_amount: '',
  deposit_due: 'Upon signing',
  progress_amount: '',
  progress_due: '',
  final_amount: '',
  final_due: 'Upon substantial completion',
  start_date: props.startDate?.split('T')[0] || '',
  end_date: props.endDate?.split('T')[0] || '',
  warranty_period: '1 year',
  additional_terms: '',
});

export const ContractBuilderTab = ({
  jobId, jobTitle, jobNumber, clientName, clientAddress, siteAddress, startDate, endDate, budget,
}: ContractBuilderTabProps) => {
  const props = { jobId, jobTitle, jobNumber, clientName, clientAddress, siteAddress, startDate, endDate, budget };
  const [docs, setDocs] = useState<ProjectDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<ContractForm>(() => EMPTY_FORM(props));
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      const all = await listJobDocuments(jobId);
      setDocs(all.filter((d) => d.document_type === 'work_agreement'));
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [jobId]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setForm(EMPTY_FORM(props));
    setEditingId(null);
    setShowModal(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const content = generateContractHtml(form, props);
      if (editingId) {
        await updateProjectDocument(editingId, { title: form.title, content });
      } else {
        await createProjectDocument({ job_id: Number(jobId), document_type: 'work_agreement', title: form.title, content });
      }
      setShowModal(false);
      await load();
    } catch { /* silent */ }
    finally { setSaving(false); }
  };

  const handleStatus = async (id: number, status: string) => {
    const opts: { issued_date?: string; signed_date?: string } = {};
    if (status === 'issued') opts.issued_date = new Date().toISOString().split('T')[0];
    if (status === 'signed') opts.signed_date = new Date().toISOString().split('T')[0];
    await updateDocumentStatus(id, status, opts);
    await load();
  };

  // CR #128 — share/hide this document in the client portal.
  const toggleClientVisibility = async (doc: ProjectDocument) => {
    const next = doc.visible_to_client === 0 ? 1 : 0;
    setDocs(prev => prev.map(d => d.id === doc.id ? { ...d, visible_to_client: next } : d));
    try {
      await updateProjectDocument(doc.id, { visible_to_client: next });
    } catch {
      setDocs(prev => prev.map(d => d.id === doc.id ? { ...d, visible_to_client: doc.visible_to_client } : d));
    }
  };

  const handleDelete = async (id: number) => {
    await deleteProjectDocument(id);
    setConfirmDelete(null);
    await load();
  };

  const F = (field: keyof ContractForm) => ({
    value: form[field],
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((prev) => ({ ...prev, [field]: e.target.value })),
  });

  const inputClass = 'w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary/30';
  const labelClass = 'block text-xs font-semibold text-gray-600 mb-1';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">{docs.length === 0 ? 'No work agreements yet.' : `${docs.length} agreement${docs.length !== 1 ? 's' : ''}`}</p>
        <button onClick={openNew} className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-white rounded-lg text-sm font-semibold hover:bg-primary/90">
          <span className="material-symbols-outlined text-base">add</span>New Agreement
        </button>
      </div>

      {loading ? (
        <div className="space-y-2">{[1, 2].map((i) => <div key={i} className="h-16 rounded-xl bg-gray-100 animate-pulse" />)}</div>
      ) : docs.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-gray-200 p-8 text-center text-gray-400">
          <span className="material-symbols-outlined text-3xl mb-2 block">description</span>
          <p className="text-sm">Create a work agreement to share with the client</p>
        </div>
      ) : (
        <div className="space-y-2">
          {docs.map((doc) => (
            <div key={doc.id} className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-4">
              <span className="material-symbols-outlined text-primary text-xl">description</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">{doc.title}</p>
                <p className="text-xs text-gray-500">
                  {new Date(doc.created_at).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' })}
                  {doc.issued_date ? ` · Issued ${fmtDate(doc.issued_date)}` : ''}
                  {doc.signed_date ? ` · Signed ${fmtDate(doc.signed_date)}` : ''}
                </p>
              </div>
              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${STATUS_COLORS[doc.status]}`}>{doc.status}</span>
              {/* CR #128: client-portal visibility toggle */}
              <button
                onClick={() => toggleClientVisibility(doc)}
                title={doc.visible_to_client === 0
                  ? 'Hidden from the client portal — click to share'
                  : 'Visible in the client portal — click to hide'}
                className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold border ${
                  doc.visible_to_client === 0
                    ? 'border-gray-200 text-gray-400 hover:bg-gray-50'
                    : 'border-green-300 bg-green-50 text-green-700 hover:bg-green-100'
                }`}
              >
                <span className="material-symbols-outlined text-base">
                  {doc.visible_to_client === 0 ? 'visibility_off' : 'visibility'}
                </span>
                <span className="hidden sm:inline">{doc.visible_to_client === 0 ? 'Hidden' : 'Client'}</span>
              </button>
              <div className="flex items-center gap-1">
                <a href={getDocumentRenderUrl(doc.id)} target="_blank" rel="noopener noreferrer"
                  className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100" title="View / Print">
                  <span className="material-symbols-outlined text-base">open_in_new</span>
                </a>
                {doc.status === 'draft' && (
                  <button onClick={() => handleStatus(doc.id, 'issued')} title="Mark as issued"
                    className="p-1.5 rounded-lg text-blue-600 hover:bg-blue-50 text-xs font-semibold">
                    <span className="material-symbols-outlined text-base">send</span>
                  </button>
                )}
                {doc.status === 'issued' && (
                  <button onClick={() => handleStatus(doc.id, 'signed')} title="Mark as signed"
                    className="p-1.5 rounded-lg text-green-600 hover:bg-green-50">
                    <span className="material-symbols-outlined text-base">task_alt</span>
                  </button>
                )}
                {doc.status !== 'void' && doc.status !== 'signed' && (
                  <button onClick={() => setConfirmDelete(doc.id)} title="Delete"
                    className="p-1.5 rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-500">
                    <span className="material-symbols-outlined text-base">delete</span>
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Build / Edit modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 overflow-y-auto py-8 px-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-900">{editingId ? 'Edit Work Agreement' : 'New Work Agreement'}</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-700">
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <div className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2"><label className={labelClass}>Contract Title</label><input className={inputClass} {...F('title')} /></div>
                <div><label className={labelClass}>Effective Date</label><input type="date" className={inputClass} {...F('effective_date')} /></div>
                <div><label className={labelClass}>Warranty Period</label><input className={inputClass} placeholder="e.g. 1 year" {...F('warranty_period')} /></div>
                <div><label className={labelClass}>Estimated Start</label><input type="date" className={inputClass} {...F('start_date')} /></div>
                <div><label className={labelClass}>Estimated Completion</label><input type="date" className={inputClass} {...F('end_date')} /></div>
              </div>
              <div>
                <label className={labelClass}>Scope of Work</label>
                <textarea rows={5} className={inputClass} placeholder="Describe the work to be performed…" {...F('scope_summary')} />
              </div>
              <div>
                <label className={labelClass}>Total Contract Price ($)</label>
                <input type="number" min="0" step="0.01" className={inputClass} placeholder="0.00" {...F('contract_price')} />
              </div>
              <div className="border border-gray-100 rounded-xl p-4 space-y-3">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Payment Schedule (optional)</p>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className={labelClass}>Deposit Amount ($)</label><input type="number" min="0" className={inputClass} placeholder="0.00" {...F('deposit_amount')} /></div>
                  <div><label className={labelClass}>Deposit Due</label><input className={inputClass} placeholder="Upon signing" {...F('deposit_due')} /></div>
                  <div><label className={labelClass}>Progress Payment ($)</label><input type="number" min="0" className={inputClass} placeholder="0.00" {...F('progress_amount')} /></div>
                  <div><label className={labelClass}>Progress Payment Due</label><input className={inputClass} placeholder="At framing completion…" {...F('progress_due')} /></div>
                  <div><label className={labelClass}>Final Payment ($)</label><input type="number" min="0" className={inputClass} placeholder="0.00" {...F('final_amount')} /></div>
                  <div><label className={labelClass}>Final Payment Due</label><input className={inputClass} placeholder="Upon substantial completion" {...F('final_due')} /></div>
                </div>
              </div>
              <div>
                <label className={labelClass}>Additional Terms & Conditions (optional)</label>
                <textarea rows={4} className={inputClass} placeholder="Any additional clauses, exclusions, or special conditions…" {...F('additional_terms')} />
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm font-medium">Cancel</button>
              <button onClick={handleSave} disabled={saving || !form.title.trim() || !form.scope_summary.trim()}
                className="px-4 py-2 rounded-lg bg-primary text-white text-sm font-bold hover:bg-primary/90 disabled:opacity-50">
                {saving ? 'Saving…' : editingId ? 'Update' : 'Generate & Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm delete */}
      {confirmDelete !== null && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full shadow-xl">
            <h3 className="font-bold text-gray-900 mb-2">Delete agreement?</h3>
            <p className="text-sm text-gray-600 mb-5">This will permanently remove the document. This cannot be undone.</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setConfirmDelete(null)} className="px-4 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm">Cancel</button>
              <button onClick={() => handleDelete(confirmDelete)} className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-bold hover:bg-red-700">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
